import React, { useState, useEffect, useMemo, useCallback } from "react";
import config from "../../Config";
import axios from "axios";
import Spinner from "react-bootstrap/Spinner";
import StudentMenu from "./StudentMenu";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { NavLink } from "react-router-dom";
import { useParams } from "react-router-dom";
import moment from "moment";
import Pagination from "../General/Pagination";
import StudentLedgerHeader from "./StudentLedgerHeader";
import YearsDropDown from "../Shared/YearsDropDown";
import MonthsDropDown from "../Shared/MonthsDropDown";
import {
  formatMoney,
  sumLedgerRows,
  computeLedgerTotals,
  getChallanReceivableAmount,
  getChallanDueAmount,
  getLatestPostingSlice,
  getChallanSettlement,
  groupLedgerByChallan,
} from "./ledgerCalcs";

function StudentLedger2() {
  const params = useParams();
  const studentIdParam = params.id;

  const [AccessKey] = useState(localStorage.getItem("AccessKey"));
  const [UserID] = useState(localStorage.getItem("ID"));
  const [MemberType] = useState(localStorage.getItem("MemberType"));

  const [loading, setLoading] = useState(false);

  const [fullLedger, setFullLedger] = useState([]);
  const [filteredLedger, setFilteredLedger] = useState([]);
  const [filterMonth, setFilterMonth] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterChallanNo, setFilterChallanNo] = useState("");

  const [showLedgers, setShowLedgers] = useState(true);
  const [activeTab, setActiveTab] = useState("adLedger");
  // "grouped" walks voucher by voucher and shows the arrears chain;
  // "flat" is the original raw row listing.
  const [ledgerViewMode, setLedgerViewMode] = useState("grouped");
  const [openChallans, setOpenChallans] = useState({});
  const [challans, setChallans] = useState([]);
  const [loadingChallans, setLoadingChallans] = useState(false);

  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const totalRecords = filteredLedger.length;

  const listOfLedger = useMemo(() => {
    return filteredLedger.slice(
      (pageNo - 1) * pageSize,
      pageNo * pageSize
    );
  }, [filteredLedger, pageNo, pageSize]);

  /** Net totals: outstanding uses latest row per fee code; received sums all historical rows. */
  const totalsNetFiltered = useMemo(() => {
    return computeLedgerTotals(filteredLedger);
  }, [filteredLedger]);

  const totalsNetAll = useMemo(() => {
    return computeLedgerTotals(fullLedger);
  }, [fullLedger]);

  const totalReceivedFromPaidChallans = useMemo(() => {
    return challans
      .filter((c) => c.Is_Paid)
      .reduce((sum, c) => sum + getChallanDueAmount(c), 0);
  }, [challans]);

  const latestPosting = useMemo(
    () => getLatestPostingSlice(fullLedger),
    [fullLedger]
  );

  const latestPostingTotals = useMemo(() => {
    return sumLedgerRows(latestPosting.rows);
  }, [latestPosting.rows]);

  /** Latest generated challan due amount — matches the fee voucher total. */
  const latestChallanReceivable = useMemo(() => {
    const fromChallan = getChallanReceivableAmount(challans);
    if (fromChallan != null) return fromChallan;
    return latestPostingTotals.receiveable;
  }, [challans, latestPostingTotals.receiveable]);

  const filteredChallanReceivable = useMemo(() => {
    const fromChallan = getChallanReceivableAmount(challans, {
      challanNo: filterChallanNo?.trim() || undefined,
      month: filterMonth || undefined,
      year: filterYear || undefined,
    });
    if (fromChallan != null) return fromChallan;
    if (filterChallanNo?.trim() || (filterMonth && filterYear)) {
      return sumLedgerRows(filteredLedger).receiveable;
    }
    return latestChallanReceivable;
  }, [
    challans,
    filterChallanNo,
    filterMonth,
    filterYear,
    filteredLedger,
    latestChallanReceivable,
  ]);

  const challanGroups = useMemo(
    () => groupLedgerByChallan(filteredLedger, challans),
    [filteredLedger, challans]
  );

  const latestChallan = useMemo(() => {
    if (!challans.length) return null;
    return challans[0];
  }, [challans]);

  const latestUnpaidChallan = useMemo(() => {
    return challans.find((c) => !c.Is_Paid) || null;
  }, [challans]);

  const filterToLatestChallan = () => {
    if (!latestPosting.challanNo) {
      toast.info("No challan number on the latest posting.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return;
    }
    setFilterChallanNo(latestPosting.challanNo);
    setFilterMonth("");
    setFilterYear("");
    setPageNo(1);
    setFilteredLedger(
      fullLedger.filter(
        (item) => (item.Challan_No || "").trim() === latestPosting.challanNo
      )
    );
    setActiveTab("adLedger");
  };

  const loadLedger = useCallback(() => {
    if (!AccessKey || !UserID || !studentIdParam) {
      setLoading(false);
      return;
    }

    setLoading(true);
    const data = {
      AccessKey,
      UserID,
      MemberType: MemberType || "Admin",
      Student_ID: parseInt(studentIdParam, 10) || 0,
    };

    axios({
      method: "post",
      url: config.base_url + "ADLedger/GetADLedgerOfStudentAll",
      headers: { "Content-Type": "application/json" },
      data: data,
    })
      .then(function (response) {
        if (response.data.status_code === 1) {
          const records = response.data.ListofRecords || [];
          setFullLedger(records);
          setFilteredLedger(records);
          setPageNo(1);
        } else {
          setFullLedger([]);
          setFilteredLedger([]);
          toast.error(response.data.status_message || "No ledger data.", {
            position: toast.POSITION.TOP_RIGHT,
          });
        }
      })
      .catch(function () {
        toast.error("Network Error..", {
          position: toast.POSITION.TOP_RIGHT,
        });
        setFullLedger([]);
        setFilteredLedger([]);
      })
      .finally(() => setLoading(false));
  }, [AccessKey, UserID, MemberType, studentIdParam]);

  const loadChallans = useCallback(() => {
    if (!AccessKey || !UserID || !studentIdParam) {
      return;
    }

    setLoadingChallans(true);
    const data = {
      AccessKey,
      UserID,
      MemberType: MemberType || "Admin",
      Student_ID: parseInt(studentIdParam, 10) || 0,
    };

    axios({
      method: "post",
      url: config.base_url + "Student/GetChallansForStudent",
      headers: { "Content-Type": "application/json" },
      data: data,
    })
      .then(function (response) {
        if (response.data.status_code === 1) {
          setChallans(response.data.ListofRecords || []);
        } else {
          setChallans([]);
        }
      })
      .catch(function () {
        setChallans([]);
      })
      .finally(() => setLoadingChallans(false));
  }, [AccessKey, UserID, MemberType, studentIdParam]);

  useEffect(() => {
    document.title = "View Student Ledger";
    loadLedger();
    loadChallans();
  }, [loadLedger, loadChallans]);

  const handlePageChange = (page) => {
    setPageNo(page);
  };

  const applyFilters = () => {
    let filtered = [...fullLedger];

    if (filterChallanNo && filterChallanNo.trim().length > 0) {
      const challan = filterChallanNo.trim();
      filtered = filtered.filter((item) => item.Challan_No === challan);
    } else if (filterMonth && filterYear) {
      const selectedMonth = parseInt(filterMonth, 10);
      const y = filterYear;
      filtered = filtered.filter((item) => {
        if (!item.PostingDate || !moment(item.PostingDate).isValid()) {
          return false;
        }
        const month = moment(item.PostingDate).month() + 1;
        const year = moment(item.PostingDate).format("YYYY");
        return month === selectedMonth && year === y;
      });
    }

    setPageNo(1);
    setFilteredLedger(filtered);
  };

  const resetFilters = () => {
    setFilterMonth("");
    setFilterYear("");
    setFilterChallanNo("");
    setPageNo(1);
    setFilteredLedger(fullLedger);
  };

  const statusBadge = (status) =>
    status === "Paid"
      ? "bg-success"
      : status === "Partial"
        ? "bg-info text-dark"
        : "bg-warning text-dark";

  /*
   * Voucher-by-voucher view.
   *
   * The school bills one voucher at a time and re-bills whatever was left unpaid as
   * Arrears on the next one, so the same debt shows up on every following month. A flat
   * row list makes that impossible to follow. Each card here states the arithmetic
   * plainly: what was newly charged, what was carried in, what was collected, and what
   * therefore rolls forward.
   */
  const renderGroupedLedger = () => {
    if (challanGroups.length === 0) {
      return (
        <div className="text-muted py-4 text-center">
          No ledger entries for this filter.
        </div>
      );
    }

    return challanGroups.map((g) => {
      const isOpen = openChallans[g.challanNo] !== false; // open unless collapsed
      const billed = g.sums.receiveable;
      return (
        <div className="card border mb-2" key={g.challanNo}>
          <div
            className="card-body py-2"
            role="button"
            onClick={() =>
              setOpenChallans((prev) => ({
                ...prev,
                [g.challanNo]: prev[g.challanNo] === false,
              }))
            }
          >
            <div className="d-flex flex-wrap align-items-center gap-2">
              <i className={`ri-arrow-${isOpen ? "down" : "right"}-s-line`}></i>
              <span className="fw-bold">{g.monthLabel || "—"}</span>
              <span className="text-muted small">#{g.challanNo}</span>
              {g.referenceType && g.referenceType !== "Monthly" ? (
                <span className="badge bg-light text-dark border">{g.referenceType}</span>
              ) : null}
              <span className={`badge ${statusBadge(g.settlement.status)}`}>
                {g.settlement.status}
              </span>

              <span className="ms-auto small text-nowrap">
                <span className="text-muted">New charges</span>{" "}
                <b>{formatMoney(g.currentCharges)}</b>
                <span className="text-muted"> + arrears </span>
                <b>{formatMoney(g.sums.arrears)}</b>
                <span className="text-muted"> = billed </span>
                <b>{formatMoney(billed)}</b>
                <span className="text-muted"> · received </span>
                <b className="text-success">{formatMoney(g.sums.received)}</b>
                <span className="text-muted"> · balance </span>
                <b className={g.sums.pending > 0 ? "text-danger" : ""}>
                  {formatMoney(g.sums.pending)}
                </b>
              </span>
            </div>

            {g.sums.arrears > 0 ? (
              <div className="small text-muted mt-1">
                <i className="ri-corner-down-right-line"></i> Includes{" "}
                <b>{formatMoney(g.sums.arrears)}</b> carried forward from earlier unpaid
                voucher(s).
              </div>
            ) : null}
            {g.sums.pending > 0 ? (
              <div className="small text-danger mt-1">
                <i className="ri-arrow-right-line"></i> <b>{formatMoney(g.sums.pending)}</b>{" "}
                still unpaid — this is what should appear as arrears on the next voucher.
              </div>
            ) : null}
          </div>

          {isOpen ? (
            <div className="table-responsive border-top">
              <table className="table table-sm mb-0">
                <thead className="table-light">
                  <tr>
                    <th>Code</th>
                    <th>Description</th>
                    <th className="text-end">Fee Posted</th>
                    <th className="text-end">Concession</th>
                    <th className="text-end">Arrears B/F</th>
                    <th className="text-end">Receivable</th>
                    <th className="text-end">Received</th>
                    <th>Received Date</th>
                    <th className="text-end">Pending</th>
                  </tr>
                </thead>
                <tbody>
                  {g.rows.map((item, i) => (
                    <tr key={item.ID != null ? `r-${item.ID}` : i}>
                      <td>{item.FeeCode}</td>
                      <td>{item.Description}</td>
                      <td className="text-end">{formatMoney(item.Fee_Posted)}</td>
                      <td className="text-end">{formatMoney(item.ConcessionAmount)}</td>
                      <td className="text-end">{formatMoney(item.Arrears)}</td>
                      <td className="text-end">{formatMoney(item.ReceiveableFee)}</td>
                      <td className="text-end">{formatMoney(item.ReceivedAmount)}</td>
                      <td>
                        {item.ReceivedDate
                          ? moment(item.ReceivedDate).format("DD/MM/YYYY")
                          : ""}
                      </td>
                      <td className="text-end">{formatMoney(item.PendingFee)}</td>
                    </tr>
                  ))}
                  <tr className="table-light fw-bold">
                    <td colSpan={2} className="text-end">
                      Voucher total
                    </td>
                    <td className="text-end">{formatMoney(g.sums.feePosted)}</td>
                    <td className="text-end">{formatMoney(g.sums.concession)}</td>
                    <td className="text-end">{formatMoney(g.sums.arrears)}</td>
                    <td className="text-end">{formatMoney(g.sums.receiveable)}</td>
                    <td className="text-end">{formatMoney(g.sums.received)}</td>
                    <td></td>
                    <td className="text-end">{formatMoney(g.sums.pending)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : null}
        </div>
      );
    });
  };

  const renderLedger = () => {
    if (listOfLedger != null && listOfLedger.length > 0) {
      return listOfLedger.map((item, index) => (
        <tr key={item.ID != null ? `id-${item.ID}-${index}` : index}>
          <td>{moment(item.PostingDate).format("MMM YYYY")}</td>
          <td>{item.Challan_No}</td>
          <td>{item.FeeCode}</td>
          <td>{item.Fee_Posted}</td>
          <td>{formatMoney(item.ConcessionAmount)}</td>
          <td>{formatMoney(item.ReceiveableFee)}</td>
          <td>{formatMoney(item.Arrears)}</td>
          <td>{item.Description}</td>
          <td>{formatMoney(item.ReceivedAmount)}</td>
          <td>
            {item.ReceivedDate
              ? moment(item.ReceivedDate).format("DD/MM/YYYY")
              : ""}
          </td>
          <td>{formatMoney(item.PendingFee)}</td>
        </tr>
      ));
    }
    return (
      <tr>
        <td colSpan="11">
          <h4
            className="mt-3"
            style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}
          >
            No Data Found
          </h4>
        </td>
      </tr>
    );
  };

  return (
    <>
      <StudentMenu />

      <ToastContainer />

      <div className="container body-content">
        <div className="page-head">
          <div className="container">
            <div className="row align-items-center">
              <div className="col-md-6">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <NavLink to="/Dashboard">Dashboard</NavLink>
                    </li>
                    <li className="breadcrumb-item">
                      <NavLink to="/student/studentdashboard">Students</NavLink>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Student Ledger
                    </li>
                  </ol>
                </nav>
                <div className="page-title">Student Ledger</div>
                <p className="text-muted small mb-0">
                  AD ledger is the source of truth. Unpaid amounts roll into the
                  next month&apos;s challan as <strong>Arrears</strong> on each
                  fee line; net outstanding uses the latest row per fee code so
                  nothing is counted twice.
                </p>
              </div>
              <div className="col-md-6">
                <div className="page-right-actions">
                  <button
                    onClick={() => {
                      setShowLedgers((prevIsActive) => !prevIsActive);
                    }}
                    className="button button-white button-icon"
                  >
                    {showLedgers ? "Hide Detail" : "Show Detail"}
                    <i className="ri-filter-3-line"></i>
                  </button>
                  {/* Carries whatever filter is applied here, so the printout matches
                      what is on screen. No filter prints the complete history. */}
                  <NavLink
                    to={`/Students/PrintStudentLedger/${studentIdParam}?${new URLSearchParams(
                      {
                        month: filterMonth || "",
                        year: filterYear || "",
                        challanNo: (filterChallanNo || "").trim(),
                      }
                    ).toString()}`}
                    target="_blank"
                    className="button button-primary button-icon ms-2"
                  >
                    Print Ledger
                    <i className="ri-printer-line"></i>
                  </NavLink>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="page-content pb-0">
          <div className="container">
            <div
              className={`collapse ${showLedgers ? "show" : ""} `}
              id="collapseFilteres"
            >
              <StudentLedgerHeader
                studentId={studentIdParam}
                outstanding={loading ? "…" : formatMoney(totalsNetAll.outstanding)}
                totalReceived={loading ? "…" : formatMoney(totalsNetAll.totalReceived)}
                latestChallan={
                  latestChallan
                    ? {
                        amount: formatMoney(getChallanDueAmount(latestChallan)),
                        label: `${moment()
                          .month((latestChallan.Month || 1) - 1)
                          .format("MMM")} ${latestChallan.Year} · ${latestChallan.Challan_No}`,
                        status: getChallanSettlement(latestChallan, fullLedger).status,
                      }
                    : null
                }
              />
            </div>
          </div>
        </div>
        <div className="page-content mt-3">
          <div className="container">
            <div className="card">
              <div className="card-body">
                <ul className="nav nav-pills mb-3">
                  <li className="nav-item">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === "adLedger" ? "active" : ""}`}
                      onClick={() => setActiveTab("adLedger")}
                    >
                      AD Ledger View
                    </button>
                  </li>
                  <li className="nav-item ms-2">
                    <button
                      type="button"
                      className={`nav-link ${activeTab === "challan" ? "active" : ""}`}
                      onClick={() => setActiveTab("challan")}
                    >
                      Challan View
                    </button>
                  </li>
                </ul>

                {activeTab === "adLedger" ? (
                  <>
                <div className="row mb-3">
                  <div className="col-md-3">
                    <label className="form-label">Filter Month</label>
                    <select
                      className="form-select"
                      value={filterMonth}
                      onChange={(e) => setFilterMonth(e.target.value)}
                    >
                      <option value="">All</option>
                      <MonthsDropDown />
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Filter Year</label>
                    <select
                      className="form-select"
                      value={filterYear}
                      onChange={(e) => setFilterYear(e.target.value)}
                    >
                      <option value="">All</option>
                      <YearsDropDown />
                    </select>
                  </div>
                  <div className="col-md-3">
                    <label className="form-label">Challan No</label>
                    <input
                      type="text"
                      className="form-control"
                      value={filterChallanNo}
                      onChange={(e) => setFilterChallanNo(e.target.value)}
                      placeholder="Exact challan number"
                    />
                  </div>
                  <div className="col-md-3 d-flex align-items-end gap-2 flex-wrap">
                    <button
                      type="button"
                      className="button button-primary"
                      onClick={applyFilters}
                    >
                      Apply
                    </button>
                    <button
                      type="button"
                      className="button button-light"
                      onClick={resetFilters}
                    >
                      Reset
                    </button>
                  </div>
                </div>

                <div className="d-flex flex-wrap justify-content-between align-items-center mb-2 gap-2">
                  <div className="btn-group btn-group-sm" role="group">
                    <button
                      type="button"
                      className={`btn ${ledgerViewMode === "grouped" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setLedgerViewMode("grouped")}
                    >
                      Voucher by Voucher
                    </button>
                    <button
                      type="button"
                      className={`btn ${ledgerViewMode === "flat" ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => setLedgerViewMode("flat")}
                    >
                      All Ledger Lines
                    </button>
                  </div>
                  <div className="small text-muted">
                    {totalRecords} ledger row(s) in current filter
                  </div>
                </div>

                {ledgerViewMode === "grouped" ? (
                  loading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" role="status"></Spinner>
                    </div>
                  ) : (
                    <>
                      {renderGroupedLedger()}
                      {filteredLedger.length > 0 ? (
                        <div className="alert alert-light border small mb-0 mt-2">
                          <b>Reading this:</b> each card is one fee voucher. New charges are
                          what that month billed; arrears are the unpaid balance re-billed
                          from earlier vouchers. A voucher&apos;s remaining balance is what
                          should appear as arrears on the next one — so the newest voucher
                          normally carries the whole outstanding amount.
                        </div>
                      ) : null}
                    </>
                  )
                ) : (
                <>
                <table className="table table-theme">
                  <thead>
                    <tr>
                      <th colSpan="11" className="text-end">
                        {totalRecords} row(s) in current filter (page{" "}
                        {pageNo} of{" "}
                        {Math.max(1, Math.ceil(totalRecords / pageSize) || 1)})
                      </th>
                    </tr>
                    <tr>
                      <th>Fee Month</th>
                      <th>Challan No</th>
                      <th>Fee Code</th>
                      <th>Fee Posted</th>
                      <th>Concession Amount</th>
                      <th>Receiveable Fee</th>
                      <th>Arrears</th>
                      <th>Description</th>
                      <th>Received Amount</th>
                      <th>Received Date</th>
                      <th>Pending Fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td className="text-center" colSpan="11">
                          <Spinner animation="border" role="status"></Spinner>
                        </td>
                      </tr>
                    ) : (
                      renderLedger()
                    )}
                    {!loading && filteredLedger.length > 0 && (
                      <tr className="table-light">
                        <td colSpan={5} className="text-end small text-muted">
                          <b>
                            Net totals — receivable: latest/matched challan;
                            outstanding: latest per fee; received: sum of all rows
                          </b>
                        </td>
                        <td>
                          <b>{formatMoney(filteredChallanReceivable)}</b>
                        </td>
                        <td>
                          <b>{formatMoney(totalsNetFiltered.currentArrears)}</b>
                        </td>
                        <td></td>
                        <td>
                          <b>{formatMoney(totalsNetFiltered.totalReceived)}</b>
                        </td>
                        <td></td>
                        <td>
                          <b>{formatMoney(totalsNetFiltered.outstanding)}</b>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
                {totalRecords > pageSize && (
                  <div className="pagination-wrap">
                    <div className="row align-items-center">
                      <div className="col-md-2 form_default">
                        <div className="d-flex align-items-center">
                          <label className="me-2" style={{ fontSize: "15px" }}>
                            Page Size
                          </label>
                          <div className="form-group">
                            <select
                              className="form-select"
                              style={{ width: "100px" }}
                              value={pageSize}
                              onChange={(e) => {
                                setPageNo(1);
                                setPageSize(parseInt(e.target.value, 10));
                              }}
                            >
                              <option value="10">10</option>
                              <option value="20">20</option>
                              <option value="50">50</option>
                              <option value="100">100</option>
                              <option value="500">500</option>
                              <option value="1000">1000</option>
                            </select>
                          </div>
                        </div>
                      </div>

                      <Pagination
                        pageNo={pageNo}
                        pageSize={pageSize}
                        totalRecords={totalRecords}
                        onPageChange={handlePageChange}
                      />
                    </div>
                  </div>
                )}
                </>
                )}
                  </>
                ) : (
                  <>
                    <div className="row g-3 mb-3">
                      <div className="col-md-4">
                        <div className="card border-0 bg-light">
                          <div className="card-body py-2">
                            <div className="small text-muted">
                              Current receivable (latest challan)
                              {latestChallan ? (
                                <span className="d-block">
                                  {latestChallan.Challan_No}
                                </span>
                              ) : null}
                            </div>
                            <div className="fw-bold">{formatMoney(latestChallanReceivable)}</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card border-0 bg-light">
                          <div className="card-body py-2">
                            <div className="small text-muted">
                              Total received (all ledger lines)
                            </div>
                            <div className="fw-bold text-success">{formatMoney(totalsNetAll.totalReceived)}</div>
                            {totalReceivedFromPaidChallans > 0 &&
                            totalReceivedFromPaidChallans !== totalsNetAll.totalReceived ? (
                              <div className="small text-muted mt-1">
                                Paid challans: {formatMoney(totalReceivedFromPaidChallans)}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="card border-0 bg-light">
                          <div className="card-body py-2">
                            <div className="small text-muted">
                              Outstanding (latest per fee)
                            </div>
                            <div className="fw-bold text-warning">{formatMoney(totalsNetAll.outstanding)}</div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <p className="small text-muted mb-2">
                      Current receivable is the due amount on the latest generated
                      challan (fee voucher). Outstanding uses the latest AD ledger row
                      per fee head. Total received sums every ledger line.
                    </p>
                    <table className="table table-theme">
                      <thead>
                        <tr>
                          <th colSpan="9" className="text-end">
                            {challans.length} challan(s) found
                          </th>
                        </tr>
                        <tr>
                          <th>Month</th>
                          <th>Year</th>
                          <th>Challan No</th>
                          <th>Dated</th>
                          <th>Due Date</th>
                          <th>Due Amount</th>
                          <th>Status</th>
                          <th>Received Date</th>
                          <th>Received Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loadingChallans ? (
                          <tr>
                            <td colSpan="9" className="text-center">
                              <Spinner animation="border" role="status" />
                            </td>
                          </tr>
                        ) : challans.length === 0 ? (
                          <tr>
                            <td colSpan="9">
                              <h4
                                className="mt-3"
                                style={{ fontSize: "15px", color: "rgba(0,0,0,0.6)" }}
                              >
                                No challan data found
                              </h4>
                            </td>
                          </tr>
                        ) : (
                          <>
                            {challans.map((item, index) => {
                              // Is_Paid is only set on a full settlement, so a part payment
                              // used to read as Unpaid with nothing received. Take the money
                              // from the ledger lines instead.
                              const settlement = getChallanSettlement(item, fullLedger);
                              const dueAmount = settlement.due;
                              const receivedAmount = settlement.received;
                              const isLatest =
                                latestChallan &&
                                item.Challan_No === latestChallan.Challan_No;
                              const isUnpaidHighlight =
                                latestUnpaidChallan &&
                                item.Challan_No === latestUnpaidChallan.Challan_No;
                              return (
                                <tr
                                  key={`${item.Challan_No || "ch"}-${index}`}
                                  className={
                                    isUnpaidHighlight
                                      ? "table-warning"
                                      : isLatest
                                        ? "table-light"
                                        : ""
                                  }
                                >
                                  <td>{item.Month}</td>
                                  <td>{item.Year}</td>
                                  <td>{item.Challan_No}</td>
                                  <td>{item.Dated ? moment(item.Dated).format("DD/MM/YYYY") : ""}</td>
                                  <td>{item.Due_Date ? moment(item.Due_Date).format("DD/MM/YYYY") : ""}</td>
                                  <td>{formatMoney(dueAmount)}</td>
                                  <td>
                                    <span
                                      className={`badge ${
                                        settlement.status === "Paid"
                                          ? "bg-success"
                                          : settlement.status === "Partial"
                                            ? "bg-info text-dark"
                                            : "bg-warning text-dark"
                                      }`}
                                    >
                                      {settlement.status}
                                    </span>
                                    {settlement.status === "Partial" ? (
                                      <div className="small text-muted">
                                        Balance {formatMoney(settlement.balance)}
                                      </div>
                                    ) : null}
                                  </td>
                                  <td>
                                    {item.Received_Date
                                      ? moment(item.Received_Date).format("DD/MM/YYYY")
                                      : ""}
                                  </td>
                                  <td>{formatMoney(receivedAmount)}</td>
                                </tr>
                              );
                            })}
                            <tr className="table-light">
                              <td colSpan="5" className="text-end small text-muted">
                                <b>Sum of challan due amounts</b> (historical; may
                                include arrears already rolled forward)
                              </td>
                              <td>
                                <b>
                                  {formatMoney(
                                    challans.reduce(
                                      (s, c) => s + getChallanDueAmount(c),
                                      0
                                    )
                                  )}
                                </b>
                              </td>
                              <td colSpan="3"></td>
                            </tr>
                            <tr className="table-warning">
                              <td colSpan="5" className="text-end small">
                                <b>Net outstanding (AD ledger — use this)</b>
                              </td>
                              <td>
                                <b>{formatMoney(totalsNetAll.outstanding)}</b>
                              </td>
                              <td colSpan="2" className="small text-muted">
                                Received (all lines): {formatMoney(totalsNetAll.totalReceived)}
                              </td>
                              <td></td>
                            </tr>
                          </>
                        )}
                      </tbody>
                    </table>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default StudentLedger2;
